<template>
  <div class="layout-column">

    <!-- Einsendenr -->

    <div class="flex-gt-sm-100 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.histologie_einsende_nr'"
                           :ref="path + '.histologie_einsende_nr'"
                           rules="min:2|trim_string"
                           v-slot="{ errors }">
        <long-input
          :color="compColor.primeColor"
          :name="path + '.histologie_einsende_nr'"
          :label="$t('FORMS_DIVERSE.HISTOLOGY_SEND_NUMBER')"
          v-model="model.histologie_einsende_nr"
          :disabled="isDisabled"
          :dense="denseFields"
          :error="errors[0]"
        ></long-input>
      </validation-provider>
    </div>

    <!-- Histologiedatum -->

    <div class="flex-gt-sm-50 flex-100 px-2 mb-4">
      <validation-provider tag="div"
                           class="layout-column"
                           :name="path + '.tumor_histologiedatum'"
                           :ref="path + '.tumor_histologiedatum'"
                           :rules="{required:isRequired||dateIsRequired,german_date:true,date_not_in_future:true,date_must_be_lesser_than_death:[deathDate, model.tumor_histologiedatum_genauigkeit]}"
                           v-slot="{ errors }">
        <lx-datepicker
            :color="compColor.textInput"
            :name="path + '.tumor_histologiedatum'"
            :label="setLabelOfRequiredFields('FORMS_DIVERSE.HISTOLOGY_DATE')"
            class="mr-1"
            :disabled="isDisabled"
            :dense="denseFields"
            @input="setDateAccuracyAfterDate"
            :error="errors[0]"
            hide-details
            v-model="model.tumor_histologiedatum"
        ></lx-datepicker>

        <!-- Render error separately to be show special required-message -->
        <div v-if="errors[0]" class="layout-row layout-align-start-center my-1 ml-2">
          <span v-if="errors[0] === $t('VALIDATION.SERVER.REQUIRED')"  class="error--text" style="font-size: 12px;">{{ $t('VALIDATION.SERVER.ERROR_REQUIRED_HISTOLOGY_DATE') }}</span>
          <span v-else class="error--text" style="font-size: 12px;">{{ errors[0] }}</span>
        </div>
      </validation-provider>
    </div>

    <!-- Datumsgenauigkeit Histologiedatum -->

    <div class="flex-gt-sm-50 flex-100 px-2 layout-row">
      <validation-provider tag="div"
                           class="flex"
                           :name="path + '.tumor_histologiedatum_genauigkeit'"
                           :ref="path + '.tumor_histologiedatum_genauigkeit'"
                           :rules="{required: isRequired || !!model.tumor_histologiedatum}"
                           v-slot="{ errors }">
        <v-select
          :color="compColor.primeColor"
          :label="setLabelOfRequiredFields('FORMS_DIVERSE.HISTOLOGY_DATE_ACCURACY')"
          v-model="model.tumor_histologiedatum_genauigkeit"
          :items="DateAccuracyTypes"
          :disabled="isDisabled || (!isRequired && !model.tumor_histologiedatum)"
          outlined
          @change="validateDateAfterChangeOfAccuracy()"
          :hide-details="!errors || errors.length <= 0"
          :dense="denseFields"
          :error-messages="errors[0]"
          :menu-props="{ bottom: true, offsetY: true }"
          :dark="compColor.isInDarkMode">
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-on="on"
                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
              <v-list-item-content class="py-0">
                <label style="cursor:pointer;"
                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                       class="lighten-4 text--darken-2">{{ item.text }}</label>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>
      </validation-provider>

      <v-tooltip bottom open-delay="1000">
        <template v-slot:activator="{ on }">
          <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-start pt-2 ml-3"
               :class="{'mt-1': !denseFields}"
               style="cursor: pointer;">
            <v-icon :color="compColor.prime" medium>info</v-icon>
          </div>
        </template>
        <p style="max-width: 400px;" class="my-1">{{ $t('FORMS_DIVERSE.TOOLTIP_DATE_ACCURACY') }}</p>
      </v-tooltip>
    </div>

    <!-- Morphologie Codes -->

    <div class="flex-100 px-2 mb-6">
      <fake-input-frame :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_CODES')"
                        :disabled="isDisabled">
        <div class="layout-column flex pa-2">
          <div v-bind:style="{'z-index': '4'}" class="layout-row flex layout-align-start-center pa-1">
            <lx-btn :disabled="isDisabled || (model.morphologie_icd_o && model.morphologie_icd_o.length === 5)"
                    icon="add_circle"
                    :color="compColor.primeColor"
                    :iconColor="'white'"
                    :label="$t('FORMS_DIVERSE.HISTOLOGY_ADD_MORPHOLOGY_CODE')"
                    :tooltip="$t('FORMS_DIVERSE.HISTOLOGY_ADD_MORPHOLOGY_CODE')"
                    iconSize="21"
                    :width="'auto'"
                    :height="35"
                    @click="addNewMorphologyCode()"
            ></lx-btn>
          </div>

          <div :key="index" v-for="(item, index) in model.morphologie_icd_o"
               class="layout-row pa-0 ma-2"
               :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
               style="border-radius:4px;"
               :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

            <div class="layout-row layout-wrap flex px-3">

              <!-- Morphologie-Code  -->

              <div class="flex-gt-sm-20 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.morphologie_icd_o.' + index + '.code'"
                                     :ref="path + '.morphologie_icd_o.' + index + '.code'"
                                     rules="required|min:2|morphology_code_auto_complete"
                                     v-slot="{ errors }">
                  <lx-autocomplete
                    :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_CODE') + ' *'"
                    v-model="item.code"
                    :error="errors[0]"
                    :hideDetails="!errors || errors.length <= 0"
                    @search-method="getDataForAutocomplete"
                    @input="setMorphology"
                    :index="index"
                    :disabled="isDisabled"
                    :dense="denseFields"
                    :results="resultSearchModel">
                    <template v-slot="slotProps">
                      <div class="layout-row">
                        <div style="width: 100px;border-radius: 4px;"
                             class="mr-2 px-2 white lighten-2 layout-column layout-align-center-center">
                          <h3>
                            <lx-text-highlight :text="slotProps.lineItem.code"
                                               :search-string="slotProps.searchText"
                                               :color="compColor.prime"></lx-text-highlight>
                          </h3>
                        </div>
                        <div class="flex">
                          <span class="body-2">
                            <lx-text-highlight :text="slotProps.lineItem.description_long" :search-string="slotProps.searchText"
                                               :color="compColor.prime"></lx-text-highlight>
                          </span>
                        </div>
                      </div>
                    </template>
                  </lx-autocomplete>
                </validation-provider>
              </div>

              <!-- Morphologie ICDO-Version -->

              <div class="flex-gt-sm-30 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.morphologie_icd_o.' + index + '.version'"
                                     :ref="path + '.morphologie_icd_o.' + index + '.version'"
                                     rules="required"
                                     v-slot="{ errors }">
                  <v-select
                    :color="compColor.primeColor"
                    :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_ICDO_VERSION') + ' *'"
                    v-model="item.version"
                    :items="icdOVersions"
                    :disabled="isDisabled"
                    outlined
                    class="mb-0"
                    @change="resetMorphology(index)"
                    :persistent-hint="true"
                    :hint="$t('FORMS_DIVERSE.CODE_VERSION_HINT')"
                    :dense="denseFields"
                    :error-messages="errors[0]"
                    :menu-props="{ bottom: true, offsetY: true }"
                    :dark="compColor.isInDarkMode">
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item v-on="on"
                                   :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                        <v-list-item-content class="py-0">
                          <label style="cursor:pointer;"
                                 :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                 class="lighten-4 text--darken-2">{{ item.text }}</label>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </validation-provider>
              </div>

              <!-- Morphologie Freitext -->

              <div class="flex-gt-sm-50 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.morphologie_icd_o.' + index + '.description'"
                                     :ref="path + '.morphologie_icd_o.' + index + '.description'"
                                     rules="">
                  <v-text-field
                    :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_ICDO_DESCRIPTION')"
                    :color="compColor.primeColor"
                    :dark="compColor.isInDarkMode"
                    v-model="item.description"
                    hide-details
                    disabled
                    class="mb-0"
                    :dense="denseFields"
                    outlined
                  ></v-text-field>
                </validation-provider>
              </div>
            </div>

            <div class="layout-column layout-align-center-center px-3"
                 :class="{'grey lighten-2': !compColor.isInDarkMode}"
                 :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
              <lx-btn :color="compColor.primeColor"
                      :iconColor="'white'"
                      iconSize="21"
                      :width="35"
                      :height="35"
                      @click="deleteSelectedItem('morphologie_icd_o', index, path + '.morphologie_icd_o.' + index + '.code')"
                      :disabled="isDisabled"
                      icon="delete_forever"></lx-btn>
            </div>
          </div>
        </div>
      </fake-input-frame>
    </div>

    <!-- Morphologie Freitext -->

    <div class="flex-gt-sm-100 flex-100 px-2 mb-6">
      <validation-provider tag="div"
                           :name="path + '.morphologie_freitext'"
                           :ref="path + '.morphologie_freitext'"
                           rules="trim_string"
                           v-slot="{ errors }">
        <long-input
            :label="$t('FORMS_DIVERSE.HISTOLOGY_MORPHOLOGY_TEXT')"
            :name="path + '.morphologie_freitext'"
            :color="compColor.primeColor"
            :dark="compColor.isInDarkMode"
            v-model="model.morphologie_freitext"
            :hideDetails="!errors || errors.length <= 0"
            :disabled="isDisabled"
            :dense="denseFields"
            :error="errors[0]"
        ></long-input>
      </validation-provider>
    </div>

    <!-- Grading -->

    <div class="layout-row flex-gt-sm-100 flex-100">
      <div class="flex-gt-sm-33 flex-100 px-2">
        <validation-provider tag="div"
                             :name="path + '.grading'"
                             :ref="path + '.grading'"
                             :rules="{required: !!model.tumor_histologiedatum}"
                             v-slot="{ errors }">
          <v-select
            :color="compColor.primeColor"
            :label="formatRequiredLabelGrading('FORMS_DIVERSE.GRADING')"
            v-model="model.grading"
            :items="GradingTypes"
            outlined
            :clearable="!model.tumor_histologiedatum"
            :hide-details="!errors || errors.length <= 0"
            :disabled="isDisabled"
            :dense="denseFields"
            :error-messages="errors[0]"
            :menu-props="{ bottom: true, offsetY: true }"
            :dark="compColor.isInDarkMode">
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-on="on"
                           :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                <v-list-item-content class="py-0">
                  <label style="cursor:pointer;"
                         :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                         class="lighten-4 text--darken-2">{{ item.text }}</label>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>
        </validation-provider>
      </div>
    </div>

    <!--   Lymphknoten   -->

    <div class="flex-100 px-2 mb-6 mt-4"><h4 :style="{color: compColor.primeColor}">Falls Lymphknoten untersucht wurden:</h4></div>

    <div class="flex-gt-sm-50 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.lk_untersucht'"
                           :ref="path + '.lk_untersucht'"
                           :rules="{integer:true, between: [0,99], nodes_must_be_greater_than: [model.lk_befallen]}"
                           v-slot="{ errors }">
        <lx-numberfield :disabled="isDisabled"
                        :dense="denseFields"
                        v-model="model.lk_untersucht"
                        :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_EXAMINED_LYMPH_NODES')"
                        separator=","
                        @input="refactorEmptyIntValue(model.lk_untersucht, null, 'lk_untersucht')"
                        :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                        persistent-hint
                        :error-messages="errors[0]"
        ></lx-numberfield>
      </validation-provider>
    </div>

    <div class="flex-gt-sm-50 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.lk_befallen'"
                           :ref="path + '.lk_befallen'"
                           :rules="{integer:true, between: [0,99], nodes_must_be_lesser_than: [model.lk_untersucht]}"
                           v-slot="{ errors }">
        <lx-numberfield :disabled="isDisabled"
                        :dense="denseFields"
                        v-model="model.lk_befallen"
                        :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_INFECTED_LYMPH_NODES')"
                        separator=","
                        @input="refactorEmptyIntValue(model.lk_befallen, null, 'lk_befallen')"
                        :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                        persistent-hint
                        :error-messages="errors[0]"
        ></lx-numberfield>
      </validation-provider>
    </div>

    <!--   Sentinelknoten   -->

    <div class="flex-gt-sm-50 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.sentinel_lk_untersucht'"
                           :ref="path + '.sentinel_lk_untersucht'"
                           :rules="{integer:true, between: [0,99], nodes_must_be_greater_than: [model.sentinel_lk_befallen]}"
                           v-slot="{ errors }">
        <lx-numberfield :disabled="isDisabled"
                        :dense="denseFields"
                        v-model="model.sentinel_lk_untersucht"
                        :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_EXAMINED_SENTINEL_NODES')"
                        separator=","
                        :hide-details="!errors || errors.length <= 0"
                        @input="refactorEmptyIntValue(model.sentinel_lk_untersucht, null, 'sentinel_lk_untersucht')"
                        :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                        persistent-hint
                        :error-messages="errors[0]"
        ></lx-numberfield>
      </validation-provider>
    </div>

    <div class="flex-gt-sm-50 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.sentinel_lk_befallen'"
                           :ref="path + '.sentinel_lk_befallen'"
                           :rules="{integer:true, between: [0,99], nodes_must_be_lesser_than: [model.sentinel_lk_untersucht]}"
                           v-slot="{ errors }">
        <lx-numberfield :disabled="isDisabled"
                        :dense="denseFields"
                        v-model="model.sentinel_lk_befallen"
                        :label="$t('FORMS_DIVERSE.HISTOLOGY_COUNT_INFECTED_SENTINEL_NODES')"
                        separator=","
                        :hide-details="!errors || errors.length <= 0"
                        @input="refactorEmptyIntValue(model.sentinel_lk_befallen, null, 'sentinel_lk_befallen')"
                        :hint="$t('FORMS_DIVERSE.HISTOLOGY_HINT_LYMPH_NODES')"
                        persistent-hint
                        :error-messages="errors[0]"
        ></lx-numberfield>
      </validation-provider>
    </div>
  </div>
</template>

<script>
import metaData from '../shared/formsMetaData'
import utils from '../../../common/utils'
import LxAutocomplete from '../../lx_auto_complete/LxAutocomplete'

export default {
  name: "Histologie",
  mixins: [metaData, utils],
  components: {
    LxAutocomplete
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    path: {
      type: String
    },
    isDisabled: {
      type: Boolean
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    validationRef: {
      type: Object,
      default: () => ({})
    },
    patientId: {
      type: String
    }
  },
  data () {
    return {
      model: {
        morphologie_codes: []
      },
      icdOVersions: null,
      icdOVersionsFromLib: {},
      GradingTypes: null,
      DateAccuracyTypes: null,
      resultSearchModel: {},
    }
  },
  created () {
    this.icdOVersions = this.filterVersions('icdO')
    this.icdOVersionsFromLib = this.formsMetaData.Morphologie_ICD_O_VersionTypes_from_lib
    this.GradingTypes = this.formsMetaData.GradingTypes
    this.DateAccuracyTypes = this.formsMetaData.DateAccuracyLong
  },
  methods: {
    /**
     * Set label of grading required if histology date is set
     *
     * @param text The text to localize
     * @returns {VueI18n.TranslateResult}
     */
    formatRequiredLabelGrading ( text) {
      let label = this.$t(text)

      // if no element is selected, set * on iknr and ersatzkode
      if (this.model.tumor_histologiedatum) {
        label += ' *'
      }

      return label
    },

    /**
     * Validate date after changing date accuracy
     */
    validateDateAfterChangeOfAccuracy () {
      this.$nextTick(() => {
        if (this.$refs[this.path + '.tumor_histologiedatum']) {
          this.$refs[this.path + '.tumor_histologiedatum'].validate()
        }
      })
    },

    /**
     * Set label in dependency of required-prop
     */
    setLabelOfRequiredFields (text) {
      const requiredElement = this.isRequired || (text === 'FORMS_DIVERSE.HISTOLOGY_DATE' && this.dateIsRequired) ? ' *' : ''
      return this.$t(text) + requiredElement
    },

    /**
     * Delete morphology-code and description after changing version
     *
     * @param index The index of morphology-element
     */
    resetMorphology (index) {
      this.$delete(this.model.morphologie_icd_o[index], 'code')
      this.$delete(this.model.morphologie_icd_o[index], 'description')
    },

    /**
     * Set date accuracy per default after date input
     */
    setDateAccuracyAfterDate () {
      // set tumor_histologiedatum_genauigkeit if not exists after changing tumor_histologiedatum in datepicker
      if (this.model.tumor_histologiedatum && !this.model.tumor_histologiedatum_genauigkeit) {
        this.$set(this.model, 'tumor_histologiedatum_genauigkeit', 'E')
      } else if (!this.isRequired && !this.model.tumor_histologiedatum) {
        this.$set(this.model, 'tumor_histologiedatum_genauigkeit', undefined)
      }
    },

    /**
     * Add new element to list of morphology-codes
     */
    addNewMorphologyCode () {
      this.$nextTick(() => {
        if (!this.model.morphologie_icd_o) {
          this.$set(this.model, 'morphologie_icd_o', [])
        }

        this.$set(this.model.morphologie_icd_o, this.model.morphologie_icd_o.length, {})
      })
    },

    /**
     * Delete selected element of histology and trigger validator for saving
     */
    deleteSelectedItem (path, index, field) {
      this.deleteItem(path, index)
      this.validationRef.fields[field].changed = true
    },

    /**
     * Get list of icd10 codes via regex in auto-complete
     *
     * @param data
     * @param index The index of selected item
     */
    getDataForAutocomplete (data, index) {
      if (data && this.model.morphologie_icd_o[index].code && data !== this.model.morphologie_icd_o[index].code) {
        this.$set(this.model.morphologie_icd_o[index], 'description', undefined)
      }

      // clear string from special characters to provide errors
      // data = this.clearStringForRegexSearch(data)
      data = (data || '').replace(/#|\\|"|%|&/g, '')

      let config = {
        opts: {
          limit: 30
        },
        query: {
          searchValue: data
        }
      }

      // if icd-10 version is set, send it to backend
      const foundVersion = this.icdOVersions.find(elem => elem.value === this.model.morphologie_icd_o[index].version)

      if (foundVersion?.misdValue) {
        config.query.version = foundVersion.misdValue
      }

      this.$http.get('/morphology?config=' + JSON.stringify(config))
          .then(res => {
            this.resultSearchModel = this.resultSearchModel = {
              result: res.data.items,
              count: res.data.count || 0,
              index: index,
              version: res.data.version
            }
          })
          .catch(this.NotifyError)
    },

    /**
     * Set morphologie code value via list form auto complete
     *
     * @param data The selected element from list in auto-complete
     * @param index The index of selected morphology code
     */
    setMorphology (data, index) {
      if (data && data.code) {
        const version = this.icdOVersionsFromLib[this.resultSearchModel.version] || this.formsMetaData.ICDO_Version

        this.$set(this.model.morphologie_icd_o[index], 'code', data.code)
        this.$set(this.model.morphologie_icd_o[index], 'description', data.description_long)
        this.$set(this.model.morphologie_icd_o[index], 'version', version)
      } else {
        if (!this.model.morphologie_icd_o[index].code || typeof data === 'string' || (this.validationRef.errors && this.validationRef.errors[this.path + '..morphologie_icd_o.' + index + '.code'] && this.validationRef.errors[this.path + '..morphologie_icd_o.' + index + '.code'].length > 0)) {
          // nextTick is necessary to trigger watcher in autocomplete on the right way
          this.$nextTick(() => {
            // delete input of morphology-code, if not selected from result list
            if (typeof data === 'string') this.$set(this.model.morphologie_icd_o[index], 'code', null)
          })

          this.$set(this.model.morphologie_icd_o[index], 'description', undefined)
        }
      }
    }
  },
  computed: {
    deathDate () {
      return this.patientId ? this.$store.getters.diagnosisBaseData(this.patientId).deathDate : undefined
    },
    dateIsRequired () {
      return Boolean(this.model.morphologie_icd_o?.length > 0 || this.model.grading || this.model.morphologie_freitext || this.model.lk_untersucht || this.model.lk_befallen || this.model.sentinel_lk_untersucht || this.model.sentinel_lk_befallen)
    }
  },
  watch: {
    model: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('input', newValue)
        }
      },
      immediate: true
    },
    value: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.model = newValue
        }
      },
      immediate: true
    }
  }
}
</script>
